/**
 * Password Strength Meter Component
 *
 * Displays password requirements and strength indicator.
 */

import { useMemo } from 'react';

interface PasswordStrengthMeterProps {
  password: string;
  showRequirements?: boolean;
}

interface Requirement {
  label: string;
  met: boolean;
}

export default function PasswordStrengthMeter({
  password,
  showRequirements = true,
}: PasswordStrengthMeterProps) {
  const { requirements, strength, score, color } = useMemo(() => {
    const reqs: Requirement[] = [
      { label: 'At least 12 characters', met: password.length >= 12 },
      { label: 'Contains uppercase letter', met: /[A-Z]/.test(password) },
      { label: 'Contains lowercase letter', met: /[a-z]/.test(password) },
      { label: 'Contains a number', met: /[0-9]/.test(password) },
      { label: 'Contains special character', met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password) },
    ];

    let calculatedScore = 0;

    if (password.length >= 12) calculatedScore += 20;
    if (password.length >= 16) calculatedScore += 10;
    if (password.length >= 20) calculatedScore += 10;
    if (/[A-Z]/.test(password)) calculatedScore += 15;
    if (/[a-z]/.test(password)) calculatedScore += 15;
    if (/[0-9]/.test(password)) calculatedScore += 15;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password)) calculatedScore += 15;

    let strengthLabel: string;
    let strengthColor: string;

    if (calculatedScore < 30) {
      strengthLabel = 'Weak';
      strengthColor = 'bg-red-500';
    } else if (calculatedScore < 50) {
      strengthLabel = 'Fair';
      strengthColor = 'bg-orange-500';
    } else if (calculatedScore < 75) {
      strengthLabel = 'Good';
      strengthColor = 'bg-yellow-500';
    } else {
      strengthLabel = 'Strong';
      strengthColor = 'bg-green-500';
    }

    return {
      requirements: reqs,
      strength: strengthLabel,
      score: Math.min(100, calculatedScore),
      color: strengthColor,
    };
  }, [password]);

  if (!password) {
    return null;
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-gray-600">Password strength</span>
          <span
            className={
              strength === 'Weak'
                ? 'text-red-600'
                : strength === 'Fair'
                  ? 'text-orange-600'
                  : strength === 'Good'
                    ? 'text-yellow-600'
                    : 'text-green-600'
            }
          >
            {strength}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${color}`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>

      {/* Requirements list */}
      {showRequirements && (
        <ul className="text-xs space-y-1">
          {requirements.map((req, index) => (
            <li
              key={index}
              className={`flex items-center gap-1.5 ${
                req.met ? 'text-green-600' : 'text-gray-500'
              }`}
            >
              {req.met ? (
                <svg
                  className="w-3.5 h-3.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" strokeWidth="2" />
                </svg>
              )}
              {req.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
